<script setup lang="ts">
  // POS V2 tabbed order workspace page (ported from -pos pages/orders.vue).
  // Deep links: ?draft=<id> focuses a tab, ?order=<id>[&mode=edit] opens a
  // persisted order in a tab.
  import { getCurrentInstance, onBeforeMount, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router/composables';
  import { OrderType } from '@/enums/OrderEnums';
  import { AlertType } from '@/interfaces/IAlert';
  import { PosV2Error } from '@/domain/pos/pos-error';
  import { usePosNavigation } from '@/composition/use-pos-navigation';
  import { usePosV2 } from '@/composition/use-pos-v2';
  import { useUi } from '@/composition/use-ui';
  import { usePosDraftsStore } from '@/stores/pos-drafts.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosV2OrderContent from '@/components/pos-v2/order/PosV2OrderContent.vue';
  import PosV2OrderTabs from '@/components/pos-v2/order/PosV2OrderTabs.vue';

  const { isPosV2Active } = usePosV2();
  const restaurantStore = useRestaurantStore();
  const posDrafts = usePosDraftsStore();
  const posNavigation = usePosNavigation();
  const route = useRoute();
  const router = useRouter();
  const ui = useUi();

  const proxy = getCurrentInstance()!.proxy;

  /**
   * Back = where the user came from (recorded at navigation time). After a
   * reload the origin is gone, so derive "up" from the active tab instead:
   * a seat order belongs to its table page, everything else to the main POS.
   */
  function goBack() {
    const posPath = `/${restaurantStore.restaurant.stringId}/pos`;

    // Only trust origins inside the current restaurant's POS: the module
    // scope survives in-app restaurant switches (no reload).
    const origin = posNavigation.getOrderWorkspaceOrigin();
    if (origin && origin.startsWith(posPath)) {
      router.push(origin).catch(() => undefined);
      return;
    }

    const draft = posDrafts.activeDraft;
    if (
      draft?.type === OrderType.Seat &&
      draft.tableId &&
      restaurantStore.tables?.some((t) => t.id === draft.tableId)
    ) {
      router.push(`${posPath}/table/${draft.tableId}`).catch(() => undefined);
      return;
    }

    router.push(posPath).catch(() => undefined);
  }

  async function applyQueryIntent() {
    const draftId =
      typeof route.query.draft === 'string' ? route.query.draft : null;
    const orderId = Number(route.query.order);
    const mode = route.query.mode === 'edit' ? 'edit' : 'view';

    if (!draftId && !Number.isFinite(orderId)) return;

    // Consume the intent so reloads/back-nav don't re-trigger it.
    router.replace({ query: {} }).catch(() => undefined);

    if (draftId) {
      posDrafts.setActiveDraft(draftId);
      return;
    }

    try {
      await posDrafts.openOrderTab({ orderId }, mode);
    } catch (error) {
      if (error instanceof PosV2Error) {
        ui.showToast(
          AlertType.Error,
          String(proxy.$t(error.i18nKey, error.params))
        );
      } else {
        // serverError surfaces the backend message and reports the exception.
        ui.serverError(error, String(proxy.$t('posV2.orderOpenError')));
      }
    }
  }

  // React to the flag at any time (owner toggle, cross-tab flip), not just at
  // mount — same pattern as PosV2TablePage.
  watch(
    isPosV2Active,
    (active) => {
      if (!active) {
        router.replace(`/${restaurantStore.restaurant.stringId}/pos`);
      }
    },
    { immediate: true }
  );

  onBeforeMount(() => {
    if (!isPosV2Active.value) return;
    applyQueryIntent();
  });
</script>

<template>
  <div class="pos-v2-order-page">
    <div class="page-header">
      <PosUiButton
        icon="arrow-left"
        color="neutral"
        variant="ghost"
        size="md"
        class="back-button"
        @click="goBack"
      />
      <PosV2OrderTabs class="header-tabs" />
    </div>

    <PosV2OrderContent class="page-content" />
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-order-page {
    @apply tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-bg-canvas;

    .page-header {
      @apply tw-flex tw-flex-shrink-0 tw-items-end tw-gap-1 tw-border-b tw-border-neutral-200 tw-bg-canvas tw-pl-1;

      .back-button {
        @apply tw-mb-1 tw-flex-shrink-0;
      }

      .header-tabs {
        @apply tw-min-w-0 tw-flex-1 tw-border-b-0;
      }
    }

    .page-content {
      @apply tw-min-h-0 tw-flex-1;
    }
  }
</style>
